/**
 * The seven remaining bespoke planning components — design/09 §8, phase `09.U2`.
 *
 * These are SKELETONS WITH REAL CONTRACTS: every prop, every type and every rule the
 * component must uphold is specified and enforced, and the rendering is complete
 * enough to be keyboard-operable, axe-clean and correct in both themes. What they do
 * not yet have is real data, which arrives with each chapter's screens (§11 U4).
 *
 * The rule each one carries, which a chapter agent must not be able to bypass:
 *
 *   HierarchyNavigator   drives every screen; multi-level select across two hierarchies
 *   ScenarioSwitcher     persistent and always visible; blocks a commit without both a
 *                        scenario and a baseline (§8, reqs/07)
 *   VarianceBridge       additive with ZERO residual, asserted (reqs/04 §4.3)
 *   ExceptionQueue       bulk-actionable — "a planner approving 400 allocations one at
 *                        a time is a design failure" (§8)
 *   RecommendationCard   reason codes shown BEFORE accept is reachable (§8)
 *   SizeRunIndicator     compact inline glyph; a broken run reads as a gap
 *   FeedStatusStrip      planners must see when a plan is running on a quarantined feed
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight, ChevronDown, GitBranch, Lock, ShieldAlert, ShieldCheck, ShieldQuestion, Clock, TriangleAlert,
} from 'lucide-react';
import { Badge, Button, Checkbox, Chip, DeltaValue, EmptyState, MOTION, StatusIndicator, Tooltip } from '../primitives';
import { FOCUS_RING, FOCUS_RING_INSET } from '../primitives/focus';
import { formatNumber, formatPercent } from '../lib/format';
import { DataTableDownload, DataTableFilterBar, DataTableHead, useTableView } from './data-table';
import { RowCapNotice } from './row-cap';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion, type ExportCell, type ExportRequest } from '../export';
import type { TFunction } from 'i18next';
import {
  assertBridgeBalances, isNumeric,
  type CommitBlockReason, type Exception, type FeedCertification, type FeedState,
  type HierarchyNode, type HierarchySelection, type Recommendation, type RecommendationDecision,
  type ScenarioContext, type SizeRun, type ThresholdClass, type VarianceBridge,
} from './types';
import { codeWords, FEED_LABEL_KEY } from './words';

// ═══ hierarchy navigator ═════════════════════════════════════════════════════

export interface HierarchyNavigatorProps {
  readonly roots: readonly HierarchyNode[];
  readonly selection: HierarchySelection;
  readonly onSelectionChange: (selection: HierarchySelection) => void;
  /** Product or location. §8: "Product AND location hierarchy tree" — two instances,
   *  one contract, because the two hierarchies are structurally identical and
   *  behaviourally identical, and a planner must not have to learn two controls. */
  readonly dimension: 'product' | 'location';
  readonly labelKey: string;
  readonly onExpand?: (nodeId: string) => void;
}

export function HierarchyNavigator({
  roots, selection, onSelectionChange, dimension, labelKey, onExpand,
}: HierarchyNavigatorProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());

  const toggle = (id: string) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id);
    else { next.add(id); onExpand?.(id); }
    setOpen(next);
  };

  const toggleSelected = (id: string) => {
    const selected = new Set(selection.nodeIds);
    if (selected.has(id)) selected.delete(id);
    else selected.add(id);
    onSelectionChange({ ...selection, nodeIds: [...selected] });
  };

  const renderNode = (node: HierarchyNode): React.JSX.Element => {
    const expanded = open.has(node.id);
    return (
      <li key={node.id} role="treeitem" aria-expanded={node.childCount > 0 ? expanded : undefined}>
        <div className="flex items-center gap-1 py-1" style={{ paddingInlineStart: 4 }}>
          {node.childCount > 0
            ? (
              <button
                type="button"
                onClick={() => toggle(node.id)}
                aria-label={t(expanded ? 'hierarchy.collapse' : 'hierarchy.expand', { node: node.label })}
                // WCAG 2.2 AA target size: 24x24 minimum. The chevron glyph is
                // 12px, so the button carries the target rather than the icon —
                // axe-core's `target-size` rule fails the route otherwise, and it
                // only surfaced once a chapter screen rendered the navigator.
                className={`flex size-6 items-center justify-center text-ink-secondary ${FOCUS_RING_INSET}`}
              >
                {expanded ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
              </button>
            )
            : <span className="inline-block size-6" aria-hidden />}
          <Checkbox
            labelKey="hierarchy.selectNode"
            checked={selection.nodeIds.includes(node.id)}
            onCheckedChange={() => toggleSelected(node.id)}
          />
          <span className="text-body text-ink-primary">{node.label}</span>
          {node.childCount > 0 && (
            <span className="text-caption text-ink-secondary font-numeric tabular-nums">{node.childCount}</span>
          )}
        </div>
        {expanded && node.children && node.children.length > 0 && (
          <ul role="group" style={{ paddingInlineStart: 16 }}>
            {node.children.map(renderNode)}
          </ul>
        )}
      </li>
    );
  };

  return (
    <nav aria-label={t(labelKey)} className="flex flex-col gap-2">
      <h3 className="text-section text-ink-primary font-display uppercase">
        {t(dimension === 'product' ? 'hierarchy.product' : 'hierarchy.location')}
      </h3>
      {roots.length === 0
        ? <EmptyState titleKey="hierarchy.empty.title" bodyKey="hierarchy.empty.body" />
        : <ul role="tree" aria-label={t(labelKey)} className="overflow-auto">{roots.map(renderNode)}</ul>}
    </nav>
  );
}

// ═══ scenario switcher ═══════════════════════════════════════════════════════

export interface ScenarioSwitcherProps {
  readonly scenarios: readonly ScenarioContext[];
  readonly active: ScenarioContext;
  readonly onActivate: (scenarioId: string) => void;
  /**
   * §8: "reqs/07 blocks commits without both [scenario name and baseline version]."
   * A non-empty list disables the commit action and states WHY — G2 §2.6 forbids a
   * silently disabled control.
   */
  readonly commitBlocks: readonly CommitBlockReason[];
  readonly onCommit?: () => void;
}

const BLOCK_KEY: Record<CommitBlockReason, string> = {
  'no-baseline': 'scenario.block.noBaseline',
  'quarantined-feed': 'scenario.block.quarantinedFeed',
  'stale-recompute': 'scenario.block.staleRecompute',
  'insufficient-permission': 'scenario.block.insufficientPermission',
};

export function ScenarioSwitcher({
  scenarios, active, onActivate, commitBlocks, onCommit,
}: ScenarioSwitcherProps): React.JSX.Element {
  const { t } = useTranslation();
  const blocked = commitBlocks.length > 0;

  return (
    // Persistent and always visible. This strip is chrome, so it sits above the data
    // plane rather than inside it.
    <div
      role="group"
      aria-label={t('scenario.switcher')}
      className="bg-surface-sunken border-hairline flex flex-wrap items-center gap-3 border-b px-3 py-2"
    >
      <GitBranch size={14} aria-hidden className="text-ink-secondary" />
      <label className="text-label text-ink-secondary" htmlFor="scenario-select">{t('scenario.label')}</label>
      <select
        id="scenario-select"
        value={active.scenarioId}
        onChange={(e) => onActivate(e.target.value)}
        className={`bg-surface-raised border-hairline text-body text-ink-primary h-6 rounded-control border px-1 ${FOCUS_RING}`}
      >
        {scenarios.map((s) => <option key={s.scenarioId} value={s.scenarioId}>{s.scenarioName}</option>)}
      </select>

      {/* BOTH are shown, always. §8 requires the switcher to show scenario name AND
          baseline version, because a commit that names only one of them is a commit
          whose provenance cannot be reconstructed. */}
      <span className="text-caption text-ink-secondary inline-flex items-center gap-1">
        {t('scenario.baseline')}
        <span className="font-numeric text-numeric text-ink-primary tabular-nums">{active.baselineVersionLabel}</span>
      </span>

      <Badge labelKey={`scenario.status.${active.status}`} />
      {active.status === 'locked' && <Lock size={12} aria-hidden className="text-ink-secondary" />}

      <span className="grow" />

      {blocked && (
        <span className="flex flex-wrap gap-1">
          {commitBlocks.map((reason) => (
            <StatusIndicator key={reason} level="critical" labelKey={BLOCK_KEY[reason]} />
          ))}
        </span>
      )}
      <Button variant="primary" size="sm" disabled={blocked} onClick={onCommit}>
        {t('scenario.commit')}
      </Button>
    </div>
  );
}

// ═══ variance bridge ═════════════════════════════════════════════════════════

export interface VarianceBridgeProps {
  readonly bridge: VarianceBridge;
  readonly onStepDrill?: (stepId: string) => void;
}

/**
 * §8: "Waterfall from last-approved to proposed, additive with zero residual."
 *
 * `assertBridgeBalances` runs on every render and THROWS. A bridge that does not
 * balance is not a cosmetic defect: it tells a planner the movement is fully explained
 * when part of it is unattributed, and that is the basis on which they approve.
 */
export function VarianceBridgeChart({ bridge, onStepDrill }: VarianceBridgeProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  assertBridgeBalances(bridge);

  const scale = useMemo(() => {
    const magnitudes = bridge.steps.map((s) => Math.abs(s.delta));
    return Math.max(1, ...magnitudes, Math.abs(bridge.toValue - bridge.fromValue));
  }, [bridge]);

  /*
    THE SHAPE IS THE BRIDGE — audit v5b D-04, D-03. It was a table whose third column held a
    150px bar; the numbers were the figure and the bars decoration. Now each driver is a bar
    diverging from a zero axis (2px ink) across the full width of the row, the signed value at
    the inline end, and the two totals set in the numeric face at 600 above and below a 2px ink
    rule (brand guide p.57: "totals … separated by a 2px ink rule"). Still a table — row
    headers, a caption, the column names for a screen reader — so the table view IS this.
  */
  return (
    <section aria-label={t('bridge.label')} className="flex flex-col gap-2">
      {/* The bridge's CSV (ADR 9010), in the bridge's own order — from, each driver, to — which
          is its meaning and is never sorted. Above the bridge, at the inline end (F7). */}
      <RowCapNotice
        shown={bridge.steps.length + 2}
        total={bridge.steps.length + 2}
        always
        exportRequest={() => bridgeRequest(bridge, t)}
      />
      <table className="w-full border-collapse">
        <caption className="sr-only">{t('bridge.caption')}</caption>
        <thead className="sr-only">
          <tr>
            <th scope="col">{t('bridge.driver')}</th>
            <th scope="col">{t('bridge.magnitude')}</th>
            <th scope="col">{t('bridge.delta')} {t(bridge.unitKey)}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-ink-primary border-b-2">
            <th scope="row" className="text-body text-ink-primary py-2 text-start font-semibold">{t(bridge.fromLabelKey)}</th>
            <td />
            <td className="font-numeric text-numeric text-ink-primary py-2 text-end font-semibold tabular-nums">
              {formatNumber(bridge.fromValue, i18n.language)}
            </td>
          </tr>
          {bridge.steps.map((step) => (
            <tr key={step.id} className="border-hairline border-b">
              <th scope="row" className="text-body text-ink-primary w-1/5 py-2 pe-4 text-start font-normal">
                {onStepDrill
                  ? (
                    <button type="button" onClick={() => onStepDrill(step.id)} className={`text-start underline ${FOCUS_RING_INSET}`}>
                      {t(step.labelKey)}
                    </button>
                  )
                  : t(step.labelKey)}
              </th>
              <td className="py-2">
                {/* Drawn from the zero axis outward on the diverging ramp: the red arm means
                    "below plan" here as everywhere else in the product (§7.3). The sign is
                    also in the value beside it, so the bar is never the only carrier. */}
                <span aria-hidden className="relative block h-4 w-full">
                  <span className="bg-ink-primary absolute inset-y-0 start-1/2 w-[2px] -translate-x-1/2 rtl:translate-x-1/2" />
                  <span
                    className={step.delta < 0 ? 'bg-diverging-1' : 'bg-diverging-5'}
                    style={{
                      position: 'absolute',
                      insetBlock: 0,
                      insetInlineStart: step.delta < 0 ? `${50 - (Math.abs(step.delta) / scale) * 50}%` : '50%',
                      width: `${(Math.abs(step.delta) / scale) * 50}%`,
                    }}
                  />
                </span>
              </td>
              <td className="w-1/6 py-2 ps-4 text-end">
                <DeltaValue value={step.delta} polarity={bridge.polarity} />
              </td>
            </tr>
          ))}
          <tr className="border-ink-primary border-t-2">
            <th scope="row" className="text-body text-ink-primary py-2 text-start font-semibold">{t(bridge.toLabelKey)}</th>
            <td />
            <td className="font-numeric text-numeric text-ink-primary py-2 text-end font-semibold tabular-nums">
              {formatNumber(bridge.toValue, i18n.language)}
            </td>
          </tr>
        </tbody>
      </table>
      {/* The residual is displayed even though it is asserted to be zero. Showing a
          zero is how a planner learns to trust that it is always zero. */}
      <p className="text-caption text-ink-secondary">
        {t('bridge.residual', { residual: formatNumber(0, i18n.language) })}
      </p>
    </section>
  );
}

/** A variance bridge as an export request: the opening value, every driver, the closing value. */
function bridgeRequest(bridge: VarianceBridge, t: TFunction): ExportRequest {
  const unit = t(bridge.unitKey);
  const rows: ExportCell[][] = [
    [{ kind: 'label', value: t(bridge.fromLabelKey), code: 'from' }, { kind: 'number', raw: bridge.fromValue }],
    ...bridge.steps.map((step): ExportCell[] => [
      { kind: 'label', value: t(step.labelKey), code: step.id },
      { kind: 'number', raw: step.delta },
    ]),
    [{ kind: 'label', value: t(bridge.toLabelKey), code: 'to' }, { kind: 'number', raw: bridge.toValue }],
  ];
  return {
    id: 'bridge',
    titleKey: 'bridge.caption',
    columns: [
      { key: 'driver', headerKey: 'bridge.driver', kind: 'label' },
      { key: 'value', headerKey: 'bridge.delta', kind: 'number', unit, polarity: bridge.polarity },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: {
      filters: [],
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{ measure: 'variance_bridge', definition: t('bridge.caption'), registerVersion: '' }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: { filters: [], sort: [], visibleColumns: ['driver', 'value'], drillPath: [], densityMode: 'compact' },
  };
}

// ═══ exception queue ═════════════════════════════════════════════════════════

/**
 * One option in the queue's reason-code vocabulary.
 *
 * `code` is the datum that is recorded and audited. `labelKey` is optional because the
 * vocabulary belongs to the QUEUE and not to this build: `reason_code` is an
 * unconstrained string in the contract, and the platform can register a code between two
 * releases of this front end. A code with no label renders as the code itself — a wrong
 * label on the bulk unit is worse than no label, because the label is what the action is
 * audited by (`docs/05` §7, and the same decision ch05 took in its own picker).
 */
export interface BulkReasonCode {
  readonly code: string;
  /** Omitted where this build has no label for the code. */
  readonly labelKey?: string;
}

export interface ExceptionQueueProps {
  readonly exceptions: readonly Exception[];
  readonly onBulkAction: (action: string, ids: readonly string[], reasonCode: string) => void;
  /**
   * The reason codes a bulk action here may be recorded under.
   *
   * Supplied by the screen, from the queue it is showing — never invented here. An empty
   * or absent vocabulary means no bulk action can be audited, and the queue says so
   * rather than offering a control whose result would be filed under a code nobody chose
   * (reqs/05 §4, `docs/05` §7, `docs/adr/0016`).
   */
  readonly reasonCodes?: readonly BulkReasonCode[];
  readonly onDrill?: (exceptionId: string) => void;
  readonly filterClasses?: readonly ThresholdClass[];
  readonly onFilterChange?: (classes: readonly ThresholdClass[]) => void;
  /** Rows whose decision was just recorded: they play the exit while the queue is re-read (`useRowExit`). */
  readonly leavingIds?: ReadonlySet<string>;
}

/**
 * §8: "The primary planner surface. Sortable, bulk-actionable, filterable by threshold
 * class." And: "Bulk approve is a first-class action; a planner approving 400
 * allocations one at a time is a design failure."
 *
 * So the bulk action bar is not behind a menu, and selecting all is one control.
 *
 * ── The reason code is a control, not a constant ────────────────────────────────────
 *
 * This component used to pass the literal `'bulk.default'` to `onBulkAction`. Every bulk
 * action taken through the shared queue was therefore recorded under a reason code nobody
 * chose — which voids the audit reqs/05 §4 and `docs/05` §7 make the reason code the unit
 * of, and corrupts `planning.programme.override_rate`, which the platform now computes
 * PER REASON CODE. A published measure whose denominator is a constant this file invented
 * is worse than a missing one.
 *
 * Three rules, taken from ch05's working picker in `src/features/exit/cadence-actions.tsx`:
 *
 *   1  **No default.** Nothing is pre-selected, and there is no "other". A pre-selected
 *      first option is the same defect with a nicer name: it is still a reason code
 *      chosen by whatever happened to sort first.
 *   2  **A code with no label renders as the code.** The vocabulary is the queue's, and a
 *      guessed label on the bulk unit misdescribes the decision in the audit trail.
 *   3  **Submit stays disabled until a code is chosen** — and the disabled state STATES
 *      why, because G2 §2.6 forbids a silently disabled control.
 *
 * Radios rather than a select, for ch05's reason: a reason code chosen from a collapsed
 * list is a reason code chosen by whatever was at the top.
 */
export function ExceptionQueue({
  exceptions, onBulkAction, reasonCodes = [], onDrill, filterClasses, onFilterChange, leavingIds,
}: ExceptionQueueProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  /** `null` until the planner chooses. There is deliberately no initial selection. */
  const [reasonCode, setReasonCode] = useState<string | null>(null);

  /**
   * Sortable and filterable by column like every table (`DataTable`, ADR 9009) — §8 asks for
   * a "sortable" queue and this is where it became one. Not capped (ADR 9008): bulk approve
   * over the whole queue is this component's contract.
   *
   * **Selection is over the VIEW.** "Select all" selects the rows the planner can see, and a
   * bulk action acts only on selected rows that are still in view: a row a filter has hidden
   * is not approved, rejected or deferred by a click on rows that are not it. The threshold
   * class chips below stay the chapter's predicate (ADR 0097) and are applied before this.
   */
  const table = useTableView(exceptions, [
    { id: 'select', headerKey: 'exceptions.select', headerClassName: 'sr-only', cell: () => null },
    {
      id: 'type', headerKey: 'exceptions.type', kind: 'text',
      value: (exception) => t(exception.typeKey, exception.typeValues ?? {}),
      cell: () => null,
    },
    { id: 'entity', headerKey: 'exceptions.entity', kind: 'text', value: (exception) => exception.entityLabel, cell: () => null },
    {
      id: 'actual', headerKey: 'exceptions.actual', kind: 'number', align: 'end',
      value: (exception) => (isNumeric(exception.actual) ? exception.actual.value : null),
      cell: () => null,
    },
    {
      id: 'expected', headerKey: 'exceptions.expected', kind: 'number', align: 'end',
      value: (exception) => (isNumeric(exception.expected) ? exception.expected.value : null),
      cell: () => null,
    },
  ], { urlKey: 'exceptions' });
  const inView = table.view;
  /** The selection that counts: selected AND still in view. */
  const chosenIds = inView.filter((e) => selected.has(e.id)).map((e) => e.id);
  const allSelected = inView.length > 0 && chosenIds.length === inView.length;

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const bulkActions = useMemo(() => {
    const chosen = inView.filter((e) => selected.has(e.id));
    if (chosen.length === 0) return [];
    // Only actions available on EVERY selected exception. Offering an action that
    // silently skips some rows is how a bulk approve becomes a partial approve.
    return chosen[0]?.actions.filter((a) => chosen.every((e) => e.actions.includes(a))) ?? [];
  }, [inView, selected]);

  if (exceptions.length === 0) {
    return <EmptyState titleKey="exceptions.empty.title" bodyKey="exceptions.empty.body" />;
  }

  return (
    <section aria-label={t('exceptions.label')} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Checkbox
          labelKey="exceptions.selectAll"
          checked={allSelected ? true : chosenIds.length > 0 ? 'indeterminate' : false}
          onCheckedChange={(checked) => setSelected(checked ? new Set(inView.map((e) => e.id)) : new Set())}
        />
        {(['good', 'warning', 'serious', 'critical'] as const).map((cls) => (
          <button
            key={cls}
            type="button"
            aria-pressed={filterClasses?.includes(cls) ?? false}
            onClick={() => onFilterChange?.(
              filterClasses?.includes(cls)
                ? filterClasses.filter((c) => c !== cls)
                : [...(filterClasses ?? []), cls],
            )}
            className={FOCUS_RING}
          >
            <Chip labelKey={`threshold.${cls}`} />
          </button>
        ))}
        <span className="grow" />
        {/* Bulk approve, first-class. A reason code is captured before commit (G2.3), and
            the buttons below cannot be pressed until it has been. */}
        {bulkActions.map((action) => (
          <Button
            key={action}
            variant={action === 'reject' ? 'destructive' : 'primary'}
            size="sm"
            disabled={reasonCode === null}
            onClick={() => { if (reasonCode !== null) onBulkAction(action, chosenIds, reasonCode); }}
          >
            {t(`exceptions.action.${action}`, { count: chosenIds.length })}
          </Button>
        ))}
      </div>

      {/* The reason code, before the action and not after it. It appears only once rows
          are selected and an action is actually available on all of them, so a queue
          nobody is acting on does not carry a control nobody needs. */}
      {bulkActions.length > 0 && (
        reasonCodes.length === 0
          ? (
            // G2 §2.6 — a control that cannot be used says why. Silently hiding the bulk
            // bar would leave a planner hunting for a button; offering it would file the
            // action under a code nobody chose.
            <StatusIndicator level="serious" labelKey="exceptions.reason.noVocabulary" />
          )
          : (
            <fieldset className="flex flex-wrap items-center gap-3">
              <legend className="text-label text-ink-secondary">{t('exceptions.reason.legend')}</legend>
              {reasonCodes.map(({ code, labelKey }) => {
                const id = `exception-reason-${code}`;
                return (
                  <span key={code} className="inline-flex items-center gap-1">
                    <input
                      type="radio"
                      id={id}
                      name="exception-bulk-reason"
                      value={code}
                      checked={reasonCode === code}
                      onChange={() => setReasonCode(code)}
                      className={`size-4 accent-ink-primary ${FOCUS_RING}`}
                    />
                    <label htmlFor={id} className="text-body text-ink-primary">
                      {/* A code this build has no label for renders as the datum it is. */}
                      {labelKey === undefined ? code : t(labelKey)}
                    </label>
                  </span>
                );
              })}
              {reasonCode === null && (
                <StatusIndicator level="warning" labelKey="exceptions.reason.required" />
              )}
            </fieldset>
          )
      )}

      <DataTableFilterBar state={table} />

      {/* The queue's CSV, of the view (ADR 9010). Not a bulk action: a file of the rows shown.
          Above the table, at the inline end (F7). */}
      <DataTableDownload state={table} id="exceptions.queue" titleKey="exceptions.caption" />

      {/* The five columns carry two numeric pairs and an entity name, and their combined
          min-content width exceeds the content area below about 1000px — at which point,
          with nothing to scroll them, the table pushed the PAGE sideways: 133px at 512px
          of viewport, 265px at 1024px and 200% zoom, on every screen that mounts this
          queue. The scroller keeps the overflow inside the panel. */}
      <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <caption className="sr-only">{t('exceptions.caption')}</caption>
        <DataTableHead state={table} rowClassName="border-hairline border-b" />
        <tbody>
          {inView.map((exception) => (
            <tr key={exception.id} className={`border-hairline border-b ${leavingIds?.has(exception.id) === true ? MOTION.rowExit : ''}`}>
              <td className="w-8 px-1">
                {/* Named for the row it selects — "Select Forecast bias beyond tolerance for
                    Sandals, resort cluster" — and heard, not seen (audit v4 G-13). */}
                <Checkbox
                  labelKey="exceptions.selectRow"
                  labelValues={{
                    exception: t(exception.typeKey, exception.typeValues ?? {}),
                    entity: exception.entityLabel,
                  }}
                  labelHidden
                  checked={selected.has(exception.id)}
                  onCheckedChange={() => toggle(exception.id)}
                />
              </td>
              <td className="py-1">
                {/* §12.6 — the threshold class carries an icon and a label, never
                    colour alone. */}
                {/* `exactOptionalPropertyTypes` is on, so an absent argument bag is an
                    absent PROP and not an explicit `undefined`. */}
                <StatusIndicator
                  level={exception.thresholdClass}
                  labelKey={exception.typeKey}
                  {...(exception.typeValues === undefined ? {} : { values: exception.typeValues })}
                />
              </td>
              <td className="text-body text-ink-primary py-1">
                {onDrill
                  ? (
                    <button type="button" onClick={() => onDrill(exception.id)} className={`underline ${FOCUS_RING_INSET}`}>
                      {exception.entityLabel}
                    </button>
                  )
                  : exception.entityLabel}
              </td>
              <td className="font-numeric text-numeric text-ink-primary py-1 text-end tabular-nums">
                {isNumeric(exception.actual) ? formatNumber(exception.actual.value, i18n.language) : t('grid.missing.notReported')}
              </td>
              <td className="font-numeric text-numeric text-ink-secondary py-1 text-end tabular-nums">
                {isNumeric(exception.expected) ? formatNumber(exception.expected.value, i18n.language) : t('grid.missing.notReported')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </section>
  );
}

// ═══ recommendation card ═════════════════════════════════════════════════════

export interface RecommendationCardProps {
  readonly recommendation: Recommendation;
  /**
   * The decision actions. Absent, the card is an EXPLANATION only — no accept, adjust or
   * reject is drawn, because the decision is taken by the queue's own disposition control
   * and a second set of buttons would be a second write path (bata-ui#148 3.3).
   */
  readonly onDecision?: (decision: RecommendationDecision, adjustedValue?: number) => void;
  readonly onBulkApply?: () => void;
  /**
   * Open the reason codes on first render. An explanation card the planner opened in order
   * to read why has nothing to disclose; the reasons count as seen.
   */
  readonly reasonsOpen?: boolean;
  /** Further facts about the recommendation, drawn between the figures and the reasons. */
  readonly children?: React.ReactNode;
}

/** The model line: version and confidence, each stated as absent where it is (reqs/00 D9). */
function modelLine(
  t: TFunction, language: string, recommendation: Recommendation,
): string {
  const { modelVersion, confidence } = recommendation;
  if (modelVersion !== null && confidence !== null) {
    return t('recommendation.model', { version: modelVersion, confidence: formatPercent(confidence, language) });
  }
  if (modelVersion !== null) return t('recommendation.modelNoConfidence', { version: modelVersion });
  if (confidence !== null) {
    return t('recommendation.confidenceNoModel', { confidence: formatPercent(confidence, language) });
  }
  return t('recommendation.modelUnpublished');
}

/**
 * §8: "AI recommendation + reason codes + accept/adjust/reject + bulk apply. The UI
 * expression of 'recommend-and-approve'." And the interaction rule: "Every AI
 * recommendation shows its reason codes BEFORE it can be accepted."
 *
 * "Before" is implemented literally: the accept action is disabled until the reason
 * codes have been rendered and acknowledged. A card with an empty reason-code list
 * cannot be accepted at all, which makes an unexplained recommendation inert rather
 * than merely discouraged.
 *
 * **Live data (bata-ui#148 3.3).** Confidence and model version are nullable and render as
 * absences, never as 0% or an empty string; the change is drawn only when both figures are
 * numbers; an empty reason list can say WHY it is empty (`reasonsAbsentKey`); and without
 * `onDecision` the card is an explanation with no actions of its own.
 */
export function RecommendationCard({
  recommendation, onDecision, onBulkApply, reasonsOpen = false, children,
}: RecommendationCardProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [reasonsSeen, setReasonsSeen] = useState(reasonsOpen);
  const hasReasons = recommendation.reasonCodes.length > 0;
  const acceptable = hasReasons && reasonsSeen;

  const delta = isNumeric(recommendation.proposed) && isNumeric(recommendation.current)
    ? recommendation.proposed.value - recommendation.current.value
    : null;
  const unitKey = recommendation.contributionUnitKey ?? recommendation.measure.unitKey;

  return (
    <article className="bg-surface-raised border-hairline flex flex-col gap-3 rounded-control border p-3">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-subsection text-ink-primary">{t(recommendation.titleKey)}</h4>
        <span className="text-caption text-ink-secondary">
          {modelLine(t, i18n.language, recommendation)}
        </span>
      </header>

      <dl className="grid grid-cols-3 gap-2">
        <div>
          <dt className="text-label text-ink-secondary">{t(recommendation.currentLabelKey ?? 'recommendation.current')}</dt>
          <dd className="font-numeric text-numeric text-ink-primary tabular-nums">
            {isNumeric(recommendation.current) ? formatNumber(recommendation.current.value, i18n.language) : <span className="text-ink-secondary">{t('absent.notStated')}</span>}
          </dd>
        </div>
        <div>
          <dt className="text-label text-ink-secondary">{t(recommendation.proposedLabelKey ?? 'recommendation.proposed')}</dt>
          <dd className="font-numeric text-numeric font-semibold text-ink-primary tabular-nums">
            {isNumeric(recommendation.proposed) ? formatNumber(recommendation.proposed.value, i18n.language) : <span className="text-ink-secondary">{t('absent.notStated')}</span>}
          </dd>
        </div>
        <div>
          <dt className="text-label text-ink-secondary">{t('recommendation.change')}</dt>
          <dd>
            {delta === null ? <span className="text-numeric text-ink-secondary">{t('absent.notComparable')}</span> : <DeltaValue value={delta} polarity={recommendation.measure.polarity} />}
          </dd>
        </div>
      </dl>

      {children}

      {/* The reason codes are rendered inline, not behind a disclosure that could be
          skipped. Opening a details element is what marks them as seen. */}
      <details open={reasonsOpen || undefined} onToggle={(e) => { if (e.currentTarget.open) setReasonsSeen(true); }}>
        <summary className={`text-label text-ink-primary cursor-pointer ${FOCUS_RING_INSET}`}>
          {t(recommendation.reasonsSummaryKey ?? 'recommendation.reasonCodes', { count: recommendation.reasonCodes.length })}
        </summary>
        {hasReasons
          ? (
            <ul className="mt-2 flex flex-col gap-1">
              {recommendation.reasonCodes.map((reason) => (
                <li key={reason.code} className="text-caption text-ink-secondary flex justify-between gap-2">
                  <span>{reason.labelValues === undefined ? t(reason.labelKey) : t(reason.labelKey, reason.labelValues)}</span>
                  <span className="font-numeric tabular-nums">
                    {t('recommendation.contribution', {
                      value: formatNumber(reason.contribution, i18n.language),
                      unit: t(unitKey),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )
          : recommendation.reasonsAbsentKey !== undefined
            ? (
              // The producer said why there are none. Stated, with an icon (§12.6), and still
              // not acceptable: an explanation of the absence is not an explanation of the number.
              <p className="text-caption text-ink-secondary mt-2 inline-flex items-center gap-1">
                <ShieldQuestion size={12} aria-hidden />
                {t(recommendation.reasonsAbsentKey)}
              </p>
            )
            : (
              <p className="text-caption text-status-critical mt-2 inline-flex items-center gap-1">
                <ShieldAlert size={12} aria-hidden />
                {t('recommendation.noReasonCodes')}
              </p>
            )}
      </details>

      {onDecision && (
        <footer className="flex flex-wrap gap-2">
          <Tooltip contentKey={acceptable ? 'recommendation.acceptHint' : 'recommendation.readReasonsFirst'}>
            <Button variant="primary" size="sm" disabled={!acceptable} onClick={() => onDecision('accept')}>
              {t('recommendation.accept')}
            </Button>
          </Tooltip>
          <Button variant="secondary" size="sm" onClick={() => onDecision('adjust')}>
            {t('recommendation.adjust')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDecision('reject')}>
            {t('recommendation.reject')}
          </Button>
          {onBulkApply && (
            <Button variant="secondary" size="sm" disabled={!acceptable} onClick={onBulkApply}>
              {t('recommendation.bulkApply')}
            </Button>
          )}
        </footer>
      )}
    </article>
  );
}

// ═══ size-run indicator ══════════════════════════════════════════════════════

export interface SizeRunIndicatorProps {
  readonly run: SizeRun;
  /** Rendered inside a grid row, so it must fit the compact 24px row height (§6). */
  readonly height?: number;
  readonly labelKey?: string;
  /**
   * `glyph` — §8's compact inline glyph, for a grid row. The default, and unchanged.
   *
   * `strip` — one readable cell per size, for a panel that is ABOUT the size run rather
   * than a row that mentions it. Audit v4 G-05: the rebalancing screen mounted the glyph as
   * a panel's whole content, under a caption promising "these cells show how a broken run
   * reads", and what rendered was a strip 8px high. The glyph was right for where §8 puts
   * it; the panel needed a different reading of the same data.
   */
  readonly variant?: 'glyph' | 'strip';
}

/**
 * §8: "Compact inline glyph showing run completeness at a glance, in grid rows."
 *
 * The glyph is a strip of cells in size order — §12.11, never lexicographic. A break
 * in the run is drawn as a GAP in the missing token, not as a light-blue cell, so
 * "not ranged" cannot be mistaken for "in stock but low" (reqs/00 D8). §7.5 calls this
 * "the single most legible view" of the problem.
 */
export function SizeRunIndicator({
  run, height = 8, labelKey = 'sizeRun.label', variant = 'glyph',
}: SizeRunIndicatorProps): React.JSX.Element {
  const { t } = useTranslation();

  const broken = run.coreSizes.filter((size) => {
    const index = run.sizes.indexOf(size);
    const value = run.availability[index];
    return value === undefined || value.kind === 'counted-zero' || value.kind === 'not-reported';
  });

  if (variant === 'strip') {
    return <SizeRunStrip run={run} broken={broken} labelKey={labelKey} />;
  }

  return (
    <span
      role="img"
      aria-label={t(labelKey, { broken: broken.length, total: run.coreSizes.length })}
      className="inline-flex items-center gap-0"
      title={t('sizeRun.title', { sizes: broken.join(', ') || t('sizeRun.complete') })}
    >
      {run.sizes.map((size, index) => {
        const value = run.availability[index];
        const isCore = run.coreSizes.includes(size);
        const cls = (() => {
          if (value === undefined || value.kind === 'not-ranged') return 'bg-missing-not-ranged';
          if (value.kind === 'not-reported') return 'bg-missing-not-reported';
          if (value.kind === 'counted-zero') return 'bg-missing-not-reported';
          return isCore ? 'bg-sequential-blue-800' : 'bg-sequential-blue-400';
        })();
        return (
          <span
            key={size}
            className={`${cls} rounded-cell`}
            style={{ width: 4, height: isCore ? height : Math.max(2, height - 3) }}
          />
        );
      })}
      {/* §12.6 — the glyph never carries the meaning alone: the count is text. */}
      {broken.length > 0 && (
        <span className="text-caption text-status-critical font-numeric tabular-nums">{broken.length}</span>
      )}
    </span>
  );
}

/**
 * The same run, one cell per size, big enough to read.
 *
 * A `<figure>` and a list rather than `role="img"`: an image's children are presentational,
 * and here every cell carries text a screen reader should reach — the size, and what is in
 * it. The summary the glyph puts in its accessible name is the visible caption instead.
 *
 * The meaning is in the TEXT and the shape, the colour only repeats it (§12.6):
 *   - a counted quantity: the number, on a bar in the ramp — darker for a core size;
 *   - a counted zero: "0", on the missing token's bar, because a core size at zero is the
 *     break;
 *   - not reported: the words "not reported" (ADR 9061, no dash), on the same bar;
 *   - not ranged: an empty, dashed cell and the words. Never a light-blue cell — reqs/00 D8,
 *     "not ranged" must not read as "in stock but low".
 */
function SizeRunStrip({ run, broken, labelKey }: {
  readonly run: SizeRun;
  readonly broken: readonly string[];
  readonly labelKey: string;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <figure className="flex flex-col gap-2">
      <figcaption className="text-caption text-ink-secondary">
        {t(labelKey, { broken: broken.length, total: run.coreSizes.length })}
      </figcaption>
      {/*
        One equal column per size, the band's full width (audit v5 N-05) — from `md` up; below
        it the cells wrap three to a row rather than squeezing a run of eight into a phone.
        The column count is the run's, never a constant: a scale has as many sizes as it has.
      */}
      <ol
        className="grid grid-cols-3 gap-1 md:grid-cols-[repeat(var(--size-count),minmax(0,1fr))]"
        style={{ '--size-count': run.sizes.length } as React.CSSProperties}
      >
        {run.sizes.map((size, index) => {
          const value = run.availability[index];
          const isCore = run.coreSizes.includes(size);
          const notRanged = value === undefined || value.kind === 'not-ranged';
          /**
           * THE HOLE — audit v5 N-05. The caption said "2 core sizes broken of 4" and the
           * strip showed no break: a zero cell differed from a full one only by the colour of
           * a 4px bar. A core size at zero or unreported is now drawn as the gap it is — a
           * critical 2px frame, an icon and the word — so it is found by looking, not by
           * reading every figure. The same test as `broken` above, so they cannot disagree.
           */
          const hole = broken.includes(size);
          const bar = notRanged
            ? undefined
            : value.kind === 'number'
              ? (isCore ? 'bg-sequential-blue-800' : 'bg-sequential-blue-400')
              : 'bg-missing-not-reported';
          const figure = notRanged
            ? t('sizeRun.state.notRanged')
            : value.kind === 'number'
              ? formatNumber(value.value, i18n.language)
              : value.kind === 'counted-zero'
                ? formatNumber(0, i18n.language)
                : t('sizeRun.state.notReported');
          return (
            <li
              key={size}
              className={[
                'flex min-w-0 flex-col gap-1 overflow-hidden rounded-cell',
                hole
                  ? 'border-status-critical bg-surface-raised border-2'
                  : notRanged ? 'border-hairline border border-dashed' : 'border-hairline bg-surface-raised border',
              ].join(' ')}
            >
              {bar !== undefined && <span aria-hidden className={`${bar} h-1 w-full`} />}
              <span className="flex flex-col px-2 pb-2 pt-1">
                {/* Wrapped, not cut (usability audit D9): «Taglia core 42» in a ten-size run is wider
                    than its cell, and the size is the part the ellipsis took. */}
                <span className="text-caption text-ink-secondary [overflow-wrap:anywhere]" data-size-label="">
                  {isCore ? t('sizeRun.coreSize', { size }) : size}
                </span>
                {/* `ink-secondary`, not `ink-muted`: ADR 0092 bars muted ink as TEXT on page
                    surfaces, where it fails AA — axe caught it on cost-to-serve. The figure is
                    `text-metric` (N-05): this panel is ABOUT these numbers. */}
                <span className={notRanged || (value !== undefined && value.kind === 'not-reported')
                  ? 'text-caption text-ink-secondary'
                  : 'text-metric font-numeric text-ink-primary tabular-nums'}
                >
                  {figure}
                </span>
                {hole ? (
                  // The colour is on the MARK and the frame; the word is ink. `status-critical`
                  // as caption text fails AA on the dark surface (axe, both rebalancing routes)
                  // — the same finding `StatusIndicator` records, answered the same way.
                  <span className="text-caption text-ink-primary inline-flex items-center gap-1 font-semibold">
                    <span className="text-status-critical inline-flex">
                      <TriangleAlert size={12} aria-hidden className="shrink-0" />
                    </span>
                    {t(value !== undefined && value.kind === 'not-reported'
                      ? 'sizeRun.state.missingNotReported'
                      : 'sizeRun.state.missing')}
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
    </figure>
  );
}

// ═══ feed status strip ═══════════════════════════════════════════════════════

export interface FeedStatusStripProps {
  readonly feeds: readonly FeedCertification[];
  readonly onFeedSelect?: (feedId: string) => void;
}

const FEED_ICON = {
  certified: ShieldCheck,
  degraded: ShieldQuestion,
  quarantined: ShieldAlert,
  awaiting: Clock,
} as const;

const FEED_LEVEL: Record<FeedState, 'good' | 'warning' | 'critical' | 'serious'> = {
  certified: 'good',
  degraded: 'warning',
  quarantined: 'critical',
  awaiting: 'serious',
};

/**
 * §8: "Certification state per feed (reqs/08) — planners must see when a plan is
 * running on a quarantined feed."
 *
 * A quarantined feed is promoted to the front of the strip and announced with
 * `role="alert"`. A planner who has to scan a row of eight green shields to find the
 * one red one has not been shown anything.
 */
export function FeedStatusStrip({ feeds, onFeedSelect }: FeedStatusStripProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ordered = useMemo(() => {
    const rank: Record<FeedState, number> = { quarantined: 0, degraded: 1, awaiting: 2, certified: 3 };
    return [...feeds].sort((a, b) => rank[a.state] - rank[b.state]);
  }, [feeds]);

  const quarantined = ordered.filter((f) => f.state === 'quarantined');

  return (
    <div
      role={quarantined.length > 0 ? 'alert' : 'status'}
      aria-label={t('feeds.label')}
      className="bg-surface-sunken border-hairline flex flex-wrap items-center gap-3 border-b px-3 py-1"
    >
      {quarantined.length > 0 && (
        <StatusIndicator level="critical" labelKey="feeds.runningOnQuarantined" />
      )}
      {ordered.map((feed) => {
        const Icon = FEED_ICON[feed.state];
        const content = (
          <span className="inline-flex items-center gap-1">
            <Icon size={12} aria-hidden />
            <span className="text-caption text-ink-primary">{codeWords(t, FEED_LABEL_KEY, feed.feedLabel)}</span>
            <span className="text-micro text-ink-secondary uppercase tracking-[0.04em]">{t(`feeds.state.${feed.state}`)}</span>
          </span>
        );
        return (
          <Tooltip
            key={feed.feedId}
            contentKey={feed.failureKey ?? 'feeds.lastLoaded'}
          >
            {onFeedSelect
              ? (
                <button
                  type="button"
                  onClick={() => onFeedSelect(feed.feedId)}
                  className={FOCUS_RING}
                  aria-label={t('feeds.itemLabel', {
                    feed: codeWords(t, FEED_LABEL_KEY, feed.feedLabel),
                    state: t(`feeds.state.${feed.state}`),
                    when: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(feed.lastLoadedAt)),
                  })}
                >
                  {content}
                </button>
              )
              : <span className={FEED_LEVEL[feed.state] === 'good' ? 'text-status-good' : ''}>{content}</span>}
          </Tooltip>
        );
      })}
    </div>
  );
}
